import React, { useState, useRef, useEffect } from 'react';
import { INDIAN_STATES } from '../../constants/indianStates.js';
import { ValidationMessage } from './Validation.js';
import { selectDropdownOptionClass, selectDropdownPanelClass } from './uiClasses.js';

/**
 * Searchable Indian state dropdown — shared by user, company, and location hub forms.
 */
const StateSelectField = ({
  label = 'State',
  value = '',
  onChange,
  error = '',
  required = false,
  disabled = false,
  placeholder = 'Search and select state',
  className = '',
  id,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const query = (open ? search : value || '').toLowerCase();
  const filteredStates = INDIAN_STATES.filter((state) =>
    state.toLowerCase().includes(query)
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (!value) setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const handleSelect = (state) => {
    onChange(state);
    setSearch('');
    setOpen(false);
  };

  const inputValue = open ? search : value || '';

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={(e) => {
          setSearch(e.target.value);
          onChange('');
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base bg-white ${
          error ? 'border-red-400' : 'border-gray-300'
        }`}
      />
      {open && filteredStates.length > 0 && (
        <div className={selectDropdownPanelClass}>
          {filteredStates.map((state) => {
            const isSelected = state === value;
            return (
            <button
              key={state}
              type="button"
              onClick={() => handleSelect(state)}
              className={selectDropdownOptionClass(isSelected)}
            >
              {state}
            </button>
            );
          })}
        </div>
      )}
      <ValidationMessage error={error} />
      {value && (
        <p className="mt-1 text-xs sm:text-sm text-gray-600">
          Selected: <span className="font-medium">{value}</span>
        </p>
      )}
    </div>
  );
};

export default StateSelectField;
